import React, { useCallback, useEffect, useState, Suspense } from 'react';
import { useDispatch } from 'react-redux';
import { Analytics } from '@trello/atlassian-analytics';
import { forNamespace } from '@trello/i18n';
import {
  PopoverProps,
  PopoverScreen,
  UsePopoverResult,
} from '@trello/nachos/popover';
import { Spinner } from '@trello/nachos/spinner';
import { useLazyComponent } from '@trello/use-lazy-component';
const {
  preloadCreateBoardData,
} = require('app/gamma/src/modules/state/ui/create-menu');
import { usePreSelectWorkspace } from './usePreSelectWorkspace';

const viewTitle = forNamespace('view title');

export enum CreateBoardPopoverScreenId {
  CreateBoard,
  CreateBoardFromTemplate,
  SelectTemplate,
}

const loadingSpinner = <Spinner centered />;

interface CreateBoardPopoverScreensProps {
  hide: UsePopoverResult<HTMLElement>['hide'];
  isVisible: PopoverProps['isVisible'];
  preSelectedWorkspaceId?: string;
  push: UsePopoverResult<HTMLElement>['push'];
}

export const CreateBoardPopoverScreens: React.FC<CreateBoardPopoverScreensProps> = (
  props,
) => {
  const { hide, isVisible, preSelectedWorkspaceId, push } = props;
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(preloadCreateBoardData());
    // Only fetch this data when the component first mounts
    // eslint-disable-next-line @trello/exhaustive-deps
  }, []);

  usePreSelectWorkspace(preSelectedWorkspaceId);

  const CreateBoardFromTemplatePopover = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "create-board-from-template-popover" */ 'app/src/components/CreateBoardFromTemplate/CreateBoard'
      ),
    {
      preload: isVisible,
      namedImport: 'CreateBoard',
    },
  );

  const CreateBoardPopoverScreen = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "create-board-popover" */ 'app/src/components/CreateBoardPopover/CreateBoardContainer'
      ),
    {
      preload: isVisible,
      namedImport: 'CreateBoardContainer',
    },
  );

  const TemplatePickerPopover = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "template-picker-popover" */ 'app/src/components/CreateBoardFromTemplate/TemplatePickerContainer'
      ),
    {
      preload: isVisible,
      namedImport: 'TemplatePickerContainer',
    },
  );

  const [selectedTemplate, setSelectedTemplate] = useState({
    id: '',
    name: '',
  });

  const handleOnSelectTemplate = useCallback(
    ({ id, name }: { id: string; name: string }) => {
      Analytics.sendUIEvent({
        action: 'clicked',
        actionSubject: 'menuItem',
        actionSubjectId: 'createFromTemplateMenuItem',
        source: 'createFromTemplateInlineDialog',
        containers: {
          board: {
            id,
          },
        },
      });
      setSelectedTemplate({
        id,
        name,
      });
      push(CreateBoardPopoverScreenId.CreateBoardFromTemplate);
    },
    [push],
  );

  const handleOnStartWithTemplate = useCallback(() => {
    push(CreateBoardPopoverScreenId.SelectTemplate);
  }, [push]);

  return (
    <>
      <PopoverScreen
        id={CreateBoardPopoverScreenId.CreateBoard}
        title={viewTitle('create board')}
      >
        <Suspense fallback={loadingSpinner}>
          <CreateBoardPopoverScreen
            onStartWithTemplate={handleOnStartWithTemplate}
            hidePopover={hide}
          />
        </Suspense>
      </PopoverScreen>
      <PopoverScreen
        id={CreateBoardPopoverScreenId.SelectTemplate}
        title={viewTitle('create-from-template')}
      >
        <Suspense fallback={loadingSpinner}>
          <TemplatePickerPopover
            hide={hide}
            onSelectTemplate={handleOnSelectTemplate}
          />
        </Suspense>
      </PopoverScreen>
      <PopoverScreen
        id={CreateBoardPopoverScreenId.CreateBoardFromTemplate}
        title={selectedTemplate.name}
      >
        <Suspense fallback={loadingSpinner}>
          <CreateBoardFromTemplatePopover
            boardId={selectedTemplate.id}
            hideOverlay={hide}
          />
        </Suspense>
      </PopoverScreen>
    </>
  );
};
